import React, { useState, useEffect } from 'react';
import {
    Layout, TrendingUp, ShieldCheck, Rocket, Code, CheckCircle2,
    Search, Users, Cloud, Zap, Target, Brain, Globe, Shield,
    HandCoins, Handshake, Leaf, Scale, Radiation
} from 'lucide-react';

// Map icons string to components
const getIcon = (iconName, size = 20) => {
    const iconProps = { size, className: "text-white" };
    switch (iconName) {
        case 'layout': return <Layout {...iconProps} />;
        case 'trending-up': return <TrendingUp {...iconProps} />;
        case 'shield-check': return <ShieldCheck {...iconProps} />;
        case 'shield': return <Shield {...iconProps} />;
        case 'rocket': return <Rocket {...iconProps} />;
        case 'code': return <Code {...iconProps} />;
        case 'search': return <Search {...iconProps} />;
        case 'users': return <Users {...iconProps} />;
        case 'cloud': return <Cloud {...iconProps} />;
        case 'zap': return <Zap {...iconProps} />;
        case 'target': return <Target {...iconProps} />;
        case 'brain': return <Brain {...iconProps} />;
        case 'globe': return <Globe {...iconProps} />;
        case 'handcoins': return <HandCoins {...iconProps} />;
        case 'handshake': return <Handshake {...iconProps} />;
        case 'leaf': return <Leaf {...iconProps} />;
        case 'scale': return <Scale {...iconProps} />;
        case 'radiation': return <Radiation {...iconProps} />;
        default: return <CheckCircle2 {...iconProps} />;
    }
};

const TimelineShowcase = ({ title, subtitle, points, currentPointIndex: externalIndex, typingStates = [] }) => {
    const [internalIndex, setInternalIndex] = useState(0);
    const isExternalControlled = externalIndex !== undefined;
    const currentPointIndex = isExternalControlled ? externalIndex : internalIndex;

    // Simulation logic used only if not externally controlled
    useEffect(() => {
        if (isExternalControlled || !points || points.length === 0) return;

        const interval = setInterval(() => {
            setInternalIndex(prev =>
                prev < points.length - 1 ? prev + 1 : prev
            );
        }, 1500);
        return () => clearInterval(interval);
    }, [isExternalControlled, points?.length]);

    if (!points || points.length === 0) return null;

    return (
        <div className="flex flex-col h-full bg-slate-900 text-slate-200 font-sans p-4 overflow-hidden">

            {/* Slide Container */}
            <div className="flex flex-col flex-1 min-h-0">

                {/* Header Section */}
                <div className="p-4 pb-3 relative z-10 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-900/0">
                    <h2 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                        {title}
                    </h2>
                    {subtitle && (
                        <p className="text-lg text-blue-200/60 max-w-lg">
                            {subtitle}
                        </p>
                    )}
                </div>

                {/* Content Area - Staircase Layout */}
                <div className="flex-1 relative p-4 pt-0 min-h-0 overflow-y-auto custom-scrollbar">

                    {/* ⚠️ TOUT EST EN rem DANS CE BLOC, ET C'EST OBLIGATOIRE.
                        Le retrait du conteneur (3.5rem) et la position de la colonne
                        (left-6 = 1.5rem) sont en rem ; les décalages du point et du
                        trait horizontal étaient, eux, en px codés en dur (-39, -33,
                        24). Les deux ne coïncidaient QUE si la racine du document
                        vaut 16 px. Sur un poste où Chrome est réglé autrement — 13,6 px
                        sur la machine de présentation — le retrait tombe à 47,6 px
                        pendant que le -39 ne bouge pas, et les points se décalent de
                        ~6 px à gauche de la colonne. Ne réintroduis aucun px ici. */}
                    <div className="relative pl-[3.5rem]">
                        {/* The Main Vertical Line (The Spine) — left 1.5rem, w-0.5 = 0.125rem,
                            donc son axe est à 1,5625rem du bord du conteneur. */}
                        <div className="absolute left-6 top-2 bottom-6 w-0.5 bg-slate-800">
                            <div
                                className="absolute top-0 left-0 w-full bg-gradient-to-b from-blue-500 via-cyan-400 to-blue-600 transition-all duration-1000 ease-out"
                                style={{ height: `${(currentPointIndex / (points.length - 1)) * 100}%` }}
                            />
                        </div>

                        <div className="space-y-4 pb-4">
                            {points.map((point, i) => {
                                const indentLevel = Math.min(i, 4);
                                /* Le pas de l'escalier, en rem (1,5rem = 24 px à racine 16). */
                                const indent = indentLevel * 1.5;

                                return (
                                    <div
                                        key={i}
                                        className={`relative transition-all duration-500 ease-out transform ${i <= currentPointIndex ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                                            }`}
                                    >
                                        {/* 1. LE POINT, POSÉ SUR L'AXE DE LA COLONNE.
                                            Sa position est CONSTANTE — l'escalier ne le
                                            décale pas, parce que c'est la carte qui est
                                            indentée, pas la ligne. Axe de la colonne :
                                            1,5rem + la moitié de ses 0,125rem = 1,5625rem
                                            du conteneur ; le point fait 1rem (w-4), son
                                            bord gauche tombe donc à 1,0625rem, soit
                                            1,0625 − 3,5 = −2,4375rem de la ligne. */}
                                        <div
                                            className={`absolute top-4 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-300 z-10 ${i <= currentPointIndex
                                                ? 'bg-slate-900 border-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.5)]'
                                                : 'bg-slate-900 border-slate-700'
                                                }`}
                                            style={{ left: '-2.4375rem' }}
                                        >
                                            <div className={`w-1.5 h-1.5 rounded-full ${i <= currentPointIndex ? 'bg-cyan-400' : 'bg-slate-700'}`} />
                                        </div>

                                        {/* 2. Le trait horizontal : part de la colonne,
                                            et c'est LUI qui s'allonge avec l'escalier. */}
                                        <div
                                            className={`absolute top-[1.45rem] h-[2px] transition-all duration-700 ${i <= currentPointIndex ? 'bg-gradient-to-r from-cyan-500/50 to-blue-500/20' : 'bg-slate-800'
                                                }`}
                                            style={{
                                                left: '-2rem',
                                                width: `${2 + indent}rem`
                                            }}
                                        />

                                        {/* 3. La carte — SEULE porteuse de l'escalier. */}
                                        <div
                                            style={{ marginLeft: `${indent}rem` }}
                                            className={`group p-4 rounded-xl border transition-all hover:scale-[1.01] hover:brightness-110 relative overflow-hidden ${i === currentPointIndex
                                            ? 'bg-blue-500/10 border-blue-500/40 shadow-lg shadow-blue-500/10'
                                            : 'bg-slate-800/40 border-slate-700/50'
                                            }`}>
                                            {i === currentPointIndex && (
                                                <div className="absolute inset-0 bg-blue-400/5 animate-pulse" />
                                            )}

                                            <div className="flex items-center gap-4 relative z-10">
                                                <div className={`p-2 rounded-lg flex-shrink-0 transition-colors ${i === currentPointIndex ? 'bg-blue-500/20 text-cyan-300' : 'bg-slate-800 text-slate-500'}`}>
                                                    {getIcon(point.icon)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-lg font-medium leading-relaxed ${i === currentPointIndex ? 'text-cyan-400' : 'text-slate-100'}`}>
                                                        {i === currentPointIndex ? (
                                                            <>
                                                                {typingStates[i] || ''}
                                                                {typingStates[i] !== point.text && <span className="inline-block w-0.5 h-4 bg-cyan-400 ml-1 animate-pulse" />}
                                                            </>
                                                        ) : i < currentPointIndex ? (
                                                            point.text
                                                        ) : null}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgba(15, 23, 42, 0.5);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(59, 130, 246, 0.3);
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(59, 130, 246, 0.5);
                }
            `}</style>
        </div>
    );
};

export default TimelineShowcase;
